<script setup lang="ts">
import { ref } from 'vue';
import { computedAsync } from '@vueuse/core';

import { AppLinkButton, AppImg } from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

import { useCoreStore } from '../../../../../../core/stores';
import type {
  AccommodationProductCardData,
  AccommodationSegmentContext,
} from '../../../../models';
import AccommodationProductCardContent from '../AccommodationProductCardContent.vue';
import AccommodationCardSelectedFooter from '../AccommodationCardSelectedFooter.vue';
import DateCardTag from '../../DateCardTag.vue';

import MobileProductCardLayout from './MobileProductCardLayout.vue';

interface AccommodationProductCardMobileProps {
  productCardData: AccommodationProductCardData;
  context: AccommodationSegmentContext;
}

const props = defineProps<AccommodationProductCardMobileProps>();

interface AccommodationProductCardMobileEmits {
  (event: 'show-details'): void;
  (event: 'show-rooms'): void;
}

const emit = defineEmits<AccommodationProductCardMobileEmits>();

const coreStore = useCoreStore();

const enableHoverColor = ref<boolean>(false);

const isHideOverviewTimelineExperiment = computedAsync<boolean>(
  () => isExperimentAsync('hide_overview_timeline'),
  false
);

const isOverviewAccommodationCardFooterExperiment = computedAsync<boolean>(
  () => isExperimentAsync('overview_accommodation_card_footer'),
  false
);
</script>

<template>
  <MobileProductCardLayout
    class="accommodation-product-card-mobile"
    @click.stop="emit('show-details')"
  >
    <template #top-section>
      <AppImg
        class="image"
        :src="productCardData.imgSrcs[0] || ''"
        alt="alternative hotel image"
        @img-load-error="coreStore.handleImageLoadError"
      />
    </template>
    <template #content>
      <AccommodationProductCardContent
        :product-card-content="props.productCardData.productCardContent"
        :context="props.context"
        :print-mode="false"
        @mouse-enter-content="enableHoverColor = true"
        @mouse-leave-content="enableHoverColor = false"
        @show-details="emit('show-details')"
        @show-rooms="emit('show-rooms')"
      />
    </template>
    <template #footer>
      <AccommodationCardSelectedFooter
        v-if="isOverviewAccommodationCardFooterExperiment"
        :ingredient-index="props.context.ingredientIndex"
      />
      <div v-else class="footer">
        <span class="label">{{ $t('planner.included') }}</span>
        <AppLinkButton
          :text="$t('planner.details')"
          size="large"
          @click="emit('show-details')"
        />
      </div>
    </template>
    <template #date>
      <DateCardTag
        v-if="isHideOverviewTimelineExperiment"
        :date="props.productCardData.date"
      />
    </template>
  </MobileProductCardLayout>
</template>

<style lang="scss" scoped>
.accommodation-product-card-mobile {
  position: relative;

  .image {
    aspect-ratio: 4 / 3;
  }

  .footer {
    display: flex;
    justify-content: space-between;
    border-top: 1px solid var(--color-secondary-93);
    padding: 20px var(--spacing-12);

    .label {
      align-self: center;
      @include font-label-l-bold;
      line-height: var(--line-heights-14);
      padding: var(--spacing-8) var(--spacing-12);
      border-radius: var(--spacing-8);
      background-color: var(--color-primary-90);
    }
  }
}
</style>
